import { PickerOverlay } from "filestack-react"
import React, { useState } from "react"
import { createPortal } from "react-dom"

import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import { AnimatedModal } from "../../../../components/shared/Modal"
import { withFilestackStoreTo } from "../../../../utils/filestackStoreTo"
import { reportFilestackUploadFailure } from "../../../../utils/reportFilestackUploadFailure"
import { generateCustomSource, switchToCustomSourceOnOpen } from "../../../WebsiteBuilder/customPickerUtils"
import { buildImageCropTransformations } from "../../utils/imageCropUtils"

const tabs = [
  { id: "uploaded", label: "Uploaded images" },
  { id: "upload", label: "Upload new" },
  { id: "stock", label: "Curated photos" }
]

/**
 * ImagePickerV2 - Reusable image picker component for WebsiteBuilderV2
 * Provides a unified interface for:
 * - Viewing and selecting from previously uploaded images
 * - Uploading new images
 * - Selecting from curated/stock photos
 * - Cropping images with custom aspect ratios
 *
 * @param {Object} props
 * @param {Object} props.practice - Practice object with allUploadedImages and defaultHeroImages
 * @param {Function} props.onImageSelect - Callback when image is selected (receives { handle, provider, imageUrl })
 * @param {boolean} props.fetching - Whether an upload/operation is in progress
 * @param {boolean} props.showCurateTab - Whether to show the curated photos tab (default: true)
 * @param {boolean} props.hideS3Images - Whether to hide S3 images from uploaded list (default: false)
 * @param {number|null} props.aspectRatio - Optional locked ratio (e.g. 1 for square). Default: freeform (any rectangle).
 * @param {string} props.currentImageUrl - Current image URL to highlight in the picker
 * @param {React.ReactNode} props.trigger - Custom trigger element (defaults to upload button)
 * @param {boolean} props.requireCrop - When false, skip Filestack forced-crop (more reliable for large phone photos). Default true.
 * @param {[number, number]|null} props.imageMax - Optional Filestack imageMax downscale before upload
 * @param {number|null} props.maxSize - Optional max file size in bytes
 * @param {string|string[]|null} props.accept - Optional Filestack accept filter (e.g. "image/*")
 * @param {boolean} [props.isOpen] - Controlled modal visibility. Omit for uncontrolled.
 * @param {Function} [props.onOpenChange] - Called with the next open boolean.
 * @param {boolean} [props.hideTrigger=false] - Skip the clickable trigger (use with controlled isOpen).
 */
export default function ImagePickerV2({
  practice,
  onImageSelect,
  fetching,
  showCurateTab = true,
  hideS3Images = false,
  aspectRatio = null,
  currentImageUrl = null,
  trigger = null,
  requireCrop = true,
  imageMax = null,
  maxSize = null,
  accept = null,
  isOpen: isOpenProp,
  onOpenChange,
  hideTrigger = false
}) {
  const isControlled = isOpenProp !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isOpen = isControlled ? Boolean(isOpenProp) : uncontrolledOpen
  const setIsOpen = (next) => {
    if (!isControlled) setUncontrolledOpen(next)
    onOpenChange?.(next)
  }
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedImageMetadata, setSelectedImageMetadata] = useState(null)
  const initialTab = practice?.allUploadedImages?.length === 0 ? "upload" : "uploaded"
  const [activeTab, setActiveTab] = useState(initialTab)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const cropTransformations = buildImageCropTransformations(aspectRatio)
  const uploadTransformations = requireCrop ? cropTransformations : null

  // Build a lookup map for images with metadata
  const imageMetadataMap = {}
  if (practice?.gallery_images_with_metadata) {
    practice.gallery_images_with_metadata.forEach((img) => {
      if (img.url) imageMetadataMap[img.url] = img
    })
  }
  if (practice?.image_assets_with_metadata) {
    practice.image_assets_with_metadata.forEach((img) => {
      if (img.url) imageMetadataMap[img.url] = img
    })
  }

  const handleImageSelect = (imageUrl) => {
    if (imageUrl === selectedImage) {
      setSelectedImage(null)
      setSelectedImageMetadata(null)
    } else {
      setSelectedImage(imageUrl)
      setSelectedImageMetadata(imageMetadataMap[imageUrl] || null)
    }
  }

  const handleSave = () => {
    if (!selectedImage) return

    setIsOpen(false)
    const handle = selectedImage.split("/").pop()
    const provider = selectedImage.includes("amazonaws.com") ? "amazon_s3" : "filestack"

    // Build the data object to pass back
    const imageData = {
      handle,
      provider,
      imageUrl: selectedImage
    }

    // If we have filestack metadata, include it for high-quality image selection
    if (selectedImageMetadata) {
      if (selectedImageMetadata.filestack_photo) {
        imageData.filestackPhoto = selectedImageMetadata.filestack_photo
      } else if (selectedImageMetadata.handles) {
        imageData.filestackPhoto = selectedImageMetadata.handles
      }
      if (selectedImageMetadata.alt_text) {
        imageData.alt = selectedImageMetadata.alt_text
      }
    }

    onImageSelect(imageData)
    setSelectedImage(null)
    setSelectedImageMetadata(null)
  }

  const handleUploadDone = (res) => {
    const file = res.filesUploaded[0]
    const handle = file.handle
    const provider = "filestack"

    setIsOpen(false)
    // Note: Newly uploaded files don't have filestack metadata yet
    // The backend will process and create versions
    onImageSelect({
      handle,
      provider,
      imageUrl: file.url
    })
  }

  const filteredTabs = showCurateTab ? tabs : tabs.filter((tab) => tab.id !== "stock")
  const uploadedImages =
    practice?.allUploadedImages?.filter((imageUrl) => !hideS3Images || !imageUrl.includes("amazonaws.com")) || []

  const renderTabContent = () => {
    switch (activeTab) {
      case "upload":
        return (
          <div className="p-4">
            <div className="relative mb-4 shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-builder-secondary">
              <p className="block leading-[20px]">Click below to upload a new image from your device.</p>
            </div>
            <FilePicker
              as="button"
              onUploadDone={handleUploadDone}
              disabled={fetching}
              transformations={uploadTransformations}
              imageMax={imageMax}
              maxSize={maxSize}
              accept={accept}
              className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 rounded border border-builder-accent bg-white px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-accent transition-colors hover:bg-builder-bg-light"
              maxFiles={1}>
              <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload
            </FilePicker>
          </div>
        )
      case "uploaded":
        return (
          <div className="h-[calc(100vh-350px)] overflow-y-auto p-4">
            {uploadedImages.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {uploadedImages.map((imageUrl, index) => {
                  const thumbUrl = imageUrl.replace("amazonaws.com", "amazonaws.com/thumbnails")
                  const isSelected = selectedImage === imageUrl
                  const isCurrent = currentImageUrl === imageUrl

                  return (
                    <div
                      key={index}
                      className={`relative cursor-pointer rounded-lg transition-all duration-200 ${
                        isSelected ? "ring-4 ring-builder-accent" : ""
                      } ${isCurrent && !isSelected ? "ring-2 ring-builder-secondary" : ""}`}
                      onClick={() => handleImageSelect(imageUrl)}>
                      <img
                        src={thumbUrl}
                        alt={`Uploaded image ${index + 1}`}
                        className="aspect-video w-full rounded-lg bg-gray-100 object-cover"
                      />
                      {isCurrent && !isSelected && (
                        <div className="absolute right-2 top-2 rounded bg-builder-accent px-2 py-1 text-xs font-bold text-white">
                          Current
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-builder-accent bg-opacity-[0.08]">
                          <div className="flex size-14 items-center justify-center rounded-full bg-white shadow-lg">
                            <svg className="size-8 text-builder-accent" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <svg className="mx-auto size-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="mt-2 text-[14px] text-builder-secondary">No images uploaded yet.</p>
                  <p className="mt-1 text-[13px] text-builder-secondary">Upload images to see them here.</p>
                </div>
              </div>
            )}
          </div>
        )
      case "stock":
        return (
          <div className="h-[calc(100vh-350px)] overflow-y-auto p-4">
            {practice?.defaultHeroImages?.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {practice.defaultHeroImages.map((imageUrl, index) => {
                  const thumbUrl = imageUrl.replace("amazonaws.com", "amazonaws.com/thumbnails")
                  const isSelected = selectedImage === imageUrl

                  return (
                    <div
                      key={index}
                      className={`cursor-pointer rounded-lg transition-all duration-200 ${
                        isSelected ? "ring-4 ring-builder-accent" : ""
                      }`}
                      onClick={() => handleImageSelect(imageUrl)}>
                      <img
                        src={thumbUrl}
                        alt={`Curated image ${index + 1}`}
                        className="aspect-video w-full rounded-lg bg-gray-100 object-cover"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-builder-accent bg-opacity-[0.08]">
                          <div className="flex size-14 items-center justify-center rounded-full bg-white shadow-lg">
                            <svg className="size-8 text-builder-accent" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-[14px] text-builder-secondary">No curated photos available.</p>
                </div>
              </div>
            )}
          </div>
        )
      default:
        return null
    }
  }

  function CropModal({ imageUrl }) {
    const customSource = generateCustomSource([{ url: imageUrl, name: "Selected Image" }])
    return createPortal(
      <PickerOverlay
        apikey={process.env.FILESTACK_API_KEY}
        onUploadDone={(res) => {
          handleUploadDone(res)
          setCropModalOpen(false)
        }}
        pickerOptions={withFilestackStoreTo({
          transformations: cropTransformations,
          fromSources: ["local_file_system", customSource],
          onOpen: switchToCustomSourceOnOpen,
          onClose: () => setCropModalOpen(false),
          allowManualRetry: true,
          onFileUploadFailed: (file) => reportFilestackUploadFailure(file, { component: "ImagePickerV2.CropModal" })
        })}
      />,
      document.body
    )
  }

  // Default trigger button
  const defaultTrigger = (
    <button
      onClick={() => setIsOpen(true)}
      disabled={fetching}
      className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 rounded border border-builder-accent bg-white px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-accent transition-colors hover:bg-builder-bg-light disabled:cursor-not-allowed disabled:opacity-50">
      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
      {initialTab === "uploaded" ? "Change Image" : "Upload Image"}
    </button>
  )

  return (
    <>
      {!hideTrigger && (trigger ? <div onClick={() => setIsOpen(true)}>{trigger}</div> : defaultTrigger)}

      {/* Image upload modal */}
      <AnimatedModal
        visible={isOpen}
        hideModal={() => {
          setIsOpen(false)
          setSelectedImage(null)
        }}
        showFooter={true}
        actionButtonCopy={activeTab === "upload" ? "Upload" : selectedImage ? "Use Image" : "Select Image"}
        footerButtons={
          activeTab === "uploaded" && selectedImage
            ? [
                {
                  label: "Crop",
                  type: "secondary",
                  onClick: () => setCropModalOpen(true),
                  show: true
                }
              ]
            : []
        }
        saveDisabled={activeTab !== "upload" && !selectedImage}
        onSave={activeTab === "upload" ? () => {} : handleSave}
        dialogClassName="max-h-[100vh] flex flex-col w-[720px] md:w-full">
        <div className="flex h-full flex-col">
          <div className="relative mb-4 w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[20px] font-bold text-builder-primary">
            <p className="block leading-[28px]">Image Selection</p>
          </div>

          {/* Tabs */}
          <div className="mb-4 flex gap-6 border-b border-builder-border-light">
            {filteredTabs.map((tab) => (
              <button
                key={tab.id}
                className={`relative pb-3 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold transition-colors ${
                  tab.id === activeTab
                    ? "border-b-2 border-builder-accent text-builder-accent"
                    : "text-builder-secondary hover:text-builder-primary"
                }`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grow overflow-hidden">{renderTabContent()}</div>
        </div>
        {/* Crop modal */}
        {cropModalOpen && selectedImage && <CropModal imageUrl={selectedImage} />}
      </AnimatedModal>
    </>
  )
}
